import { memo } from 'react'
import styled from 'styled-components'
import { Controller, useForm } from 'react-hook-form'
import * as yup from 'yup'
import { t } from 'i18next'
import { Typography } from 'antd'
import { useSearchParams } from 'react-router-dom'

import { ILoginFields } from '@interfaces'
import { themes } from '@theme'
import { Button, Input } from 'src/common'
import { yupResolver } from '@hookform/resolvers/yup'
import { authActions, useAppDispatch } from '@redux'
import { EForgotPasswordPageType, EKeyBoardCode } from '@configs'
import { LeftOutlined } from '@ant-design/icons'
import { useMutationVerifyEmail } from '@api'

interface IProps {
  isLoading?: boolean
  redirectToLogin?: () => void
}

const schemaForgotPassword = yup.object().shape({
  email: yup
    .string()
    .required(t('error:email_required'))
    .email(t('error:email_format_error')),
})

export const ForgotPasswordForm = memo((props: IProps) => {
  const { isLoading, redirectToLogin } = props
  const dispatch = useAppDispatch()
  const [searchParams, setSearchParams] = useSearchParams()

  const { control, reset, handleSubmit, getValues } = useForm<ILoginFields>({
    mode: 'onChange',
    resolver: yupResolver(schemaForgotPassword),
  })

  const { mutate: verifyEmailMutation, isPending: isVerifyEmailLoading } =
    useMutationVerifyEmail({
      handleSuccess: (res) => {
        dispatch(
          authActions.setHashState({
            data: res?.data,
            email: getValues('email'),
          })
        )
        reset({
          email: '',
        })
        setSearchParams(`type=${EForgotPasswordPageType.VERIFY_EMAIL}`)
      },
    })

  const handleForgotPassword = handleSubmit(async (value) => {
    try {
      verifyEmailMutation(value)
    } catch (err) {
      // console.log(err)
    }
  })

  return (
    <StyledForgotPasswordForm className="forgot-password__section">
      {/* @ts-ignore */}
      <form
        onSubmit={handleForgotPassword}
        className="forgot-form items-center"
      >
        <Controller
          name="email"
          control={control}
          render={({
            field: { value, onChange, onBlur },
            fieldState: { error },
          }) => {
            return (
              <Input
                placeholder={t('login:email')}
                label={t('login:email')}
                alignment="col"
                name="email"
                className="input"
                type="email"
                errors={error?.message}
                value={value}
                onChange={onChange}
                onBlur={onBlur}
                onKeyDown={(e) => {
                  if (e.code === EKeyBoardCode.SPACE && !value) {
                    e.preventDefault()
                  }
                }}
              />
            )
          }}
        />

        <div className="actions-forgot mt-5 text-center">
          <Button
            htmlType="submit"
            type="primary"
            size="large"
            className="!w-full !rounded-full mb-6"
            loading={isVerifyEmailLoading || isLoading}
          >
            {t('login:send')}
          </Button>
          <Typography.Text onClick={redirectToLogin} className="cursor-pointer">
            <LeftOutlined className="mr-1" />
            {t('login:back_to_login')}
          </Typography.Text>
        </div>
      </form>
    </StyledForgotPasswordForm>
  )
})

export const StyledForgotPasswordForm = styled.section`
  width: 100%;
  height: fit-content;
  display: flex;
  flex-direction: column;
  border-radius: 0.5rem;

  .heading {
    margin-bottom: 3.6rem;
    .heading__title {
      text-align: center;
      font-size: 3.2rem;
      font-weight: 700;
      margin-bottom: 0.8rem;
      color: ${(p) => themes.theme.light.colors.text};
    }
    .desc {
      font-size: 1.6rem;
      font-weight: 400;
      color: ${(p) => themes.theme.light.colors.subText};
    }
  }

  .actions-forgot {
    @media (min-width: 1280px) {
      display: flex;
      flex-direction: column;
      align-items: center;
      .btn {
        width: 12.8rem;
      }
    }
  }

  .forgot-form {
    width: 100%;
    display: flex;
    flex-direction: column;
    /* align-items: center; */
    .shared-input {
      margin-bottom: 2rem;
      .inner-input {
        height: 4.6rem;
      }
    }
    .submit__btn {
      @media (min-width: 1280px) {
        margin-right: 1.2rem;
        margin-bottom: 0;
      }
      margin-bottom: 1.2rem;
    }
    .forgot-password {
      display: flex;
      justify-content: flex-end;
      text-align: right;
      .forgot-text {
        font-size: 1em;
        font-weight: 400;
        color: ${(p) => themes.theme.light.colors.authText};
      }
    }

    .inner-input {
      width: 100%;
      height: 4.6rem;
    }

    .agree-policy__cb {
      margin-bottom: 1.8rem;
    }
  }
`
